<template>
    <div class="mlt-root">
        <!-- Toolbar: 가이드 + 업데이트 버튼 -->
        <div class="mlt-toolbar">
            <span class="mlt-guide">
                {{ $t('새로고침 버튼을 클릭하시거나 다른 페이지로 이동 후 재접속하시면, 경영 수준 진단 결과가 업데이트됩니다.') }}
            </span>
            <button class="mlt-refresh-btn" :disabled="loading" @click="refresh">
                <fa icon="sync-alt" :spin="loading" class="mr-1" />
                {{ $t('경영 수준 진단 결과 업데이트') }}
            </button>
        </div>

        <div class="mlt-wrap">
        <!-- Level Header Bar -->
        <div class="mlt-header">
            <div v-for="col in levelColumns"
                 :key="col.key"
                 :class="['mlt-level', { 'mlt-level--active': col.isActive }]"
                 :style="col.isActive ? { backgroundColor: col.color, '--accent': col.accent } : {}"
            >
                {{ col.label }}
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="mlt-loading">
            <b-spinner small class="mr-2"></b-spinner>
            <span>{{ $t('평가 수준 분석 중') }}...</span>
        </div>

        <!-- 조회 실패: 마지막 성공 데이터가 있으면 아래에 그대로 유지한다 -->
        <div v-if="hasError && !loading" class="mlt-empty">
            <span class="text-danger">{{ $t('경영 수준을 불러오지 못했습니다.') }}</span>
            <button class="mlt-refresh-btn ml-2" @click="refresh">{{ $t('다시 시도') }}</button>
        </div>

        <!-- 미답변 상태 -->
        <div v-else-if="!loading && scoringItems.length === 0 && !hasError" class="mlt-empty">
            <span class="text-muted">{{ $t('문항에 답변하면 경영 수준이 표시됩니다.') }}</span>
        </div>

        <!-- 전체 해당없음 -->
        <div v-else-if="currentLevel === 'na'" class="mlt-empty">
            <span class="text-muted">{{ $t('전 문항에 대해 해당없음 처리되었습니다.') }}</span>
        </div>

        <!-- Detail Area -->
        <div v-else-if="currentLevel && allDisplayItems.length > 0"
             class="mlt-detail">
            <div v-for="item in allDisplayItems" :key="item.sub_code" class="mlt-item">
                <span :class="['mlt-tag', item.tag_class]">
                    {{ item.tag_label }}
                </span>
                <span :class="['mlt-text', item.text_class || '']">{{ item.display_text }}</span>
            </div>

            <!-- 첨부 미비 안내 -->
            <div v-if="hasMissingAttachment" class="mlt-notice mlt-notice--warn">
                <fa icon="exclamation-triangle" class="mlt-notice-icon--warn" />
                {{ $t('증빙이 불충분할 경우 평가 기준에 따라 미충족 처리될 수 있습니다.') }}
            </div>

            <!-- 해당없음 안내 -->
            <div v-if="naItems.length > 0" class="mlt-notice">
                <fa icon="info-circle" class="mlt-notice-icon" />
                {{ $t("'해당없음' 판정 문항은 경영 수준 분류 시 평가에서 제외 처리 됩니다.") }}
            </div>
        </div>
        </div>
    </div>
</template>

<script>
// 사업장별 MLT 임계값 — report mixin 과 동일한 raw-data 파일을 직접 import.
//   (campaign-keyed: { "551": { "A-1-1": {...} }, ... })
import managementLevelConfig from '@shared/mixin/report/raw-data/code_level_config_hyundai_v2.json';

export default {
    name: 'ManagementLevelTable',
    props: {
        codeId: {
            type: String,
            required: true
        },
        scoringData: {
            type: Array,
            default: null
        },
        companyId: {
            type: [String, Number],
            default: null
        },
        campaignId: {
            type: [String, Number],
            default: null
        },
        projectCode: {
            type: String,
            default: null
        },
        subCodes: {
            type: Array,
            default: () => []
        },
        // subs 데이터 (답변 여부 판단용)
        subsData: {
            type: Array,
            default: null
        }
    },
    data() {
        return {
            fetchedItems: [],
            scoringItems: [],
            loading: false,
            hasError: false
        };
    },
    computed: {
        rawScoringData() {
            return this.scoringData || this.fetchedItems;
        },

        levelConfig() {
            // 사업장(campaign)별로 MLT 임계값이 다름.
            //   management-level-config.json 은 campaign-keyed 구조: { "551": { "A-1-1": {...} }, ... }.
            //   campaignId prop 으로 lookup, 없으면 null → "문항에 답변하면..." empty 상태로 표시.
            const _cid = String(this.campaignId || '');
            const _byCampaign = managementLevelConfig?.[_cid];
            if (!_byCampaign) return null;
            return _byCampaign[this.codeId] || null;
        },

        totalScore() {
            // 0618: 해당없음(NA) 문항은 만점(cap_score)으로 산정 — 전 문항 NA 코드는 currentLevel 에서 'na' 로 제외.
            //   결과보고서 mixin(codeLevelByCode/codeLevelCounts)과 동일 로직으로 화면↔PDF 일치.
            return this.scoringItems
                .reduce((sum, q) => sum + (q.not_applicable ? (q.cap_score || 0) : (q.score || 0)), 0);
        },

        hasUnsatisfiedCore() {
            return this.scoringItems.some(q => q.is_core && !q.not_applicable && !q.satisfied);
        },

        allNotApplicable() {
            return this.scoringItems.length > 0 && this.scoringItems.every(q => q.not_applicable);
        },

        currentLevel() {
            if (!this.levelConfig) return null;
            if (this.scoringItems.length === 0) return null;
            if (this.allNotApplicable) return 'na';
            if (this.hasUnsatisfiedCore) return 'major';

            const levels = this.levelConfig.levels;
            const score = this.totalScore;

            if (levels.excellent?.minScore !== undefined && score >= levels.excellent.minScore) return 'excellent';
            if (levels.conformance?.minScore !== undefined && score >= levels.conformance.minScore) return 'conformance';
            if (levels.minor?.minScore !== undefined && score >= levels.minor.minScore) return 'minor';
            return 'major';
        },

        levelColors() {
            return {
                na: { bg: '#f2f2f2', accent: '#bbb' },
                major: { bg: '#ffcccc', accent: '#e06060' },
                minor: { bg: '#ffcc99', accent: '#d48a3c' },
                conformance: { bg: '#ffff99', accent: '#c8b900' },
                excellent: { bg: '#c1f0c8', accent: '#4caf50' }
            };
        },

        levelColumns() {
            const levels = ['na', 'major', 'minor', 'conformance', 'excellent'];
            const labels = {
                na: this.$t('해당 없음'),
                major: this.$t('매우 미흡'),
                minor: this.$t('미흡'),
                conformance: this.$t('보통'),
                excellent: this.$t('우수')
            };

            return levels.map(level => ({
                key: level,
                label: labels[level],
                isActive: this.currentLevel === level,
                color: this.levelColors[level].bg,
                accent: this.levelColors[level].accent
            }));
        },

        answeredItems() {
            return this.scoringItems.filter(q => !q.not_applicable);
        },

        naItems() {
            return this.scoringItems.filter(q => q.not_applicable);
        },

        hasMissingAttachment() {
            return this.scoringItems.some(item =>
                (item.missing_attach_names && item.missing_attach_names.length > 0)
                || item.cb_missing_attach
                || item.radio_missing_attach
            );
        },

        allDisplayItems() {
            const level = this.currentLevel;
            if (!level || level === 'na') return [];

            const isBad = level === 'major' || level === 'minor';

            return this.scoringItems
                .map(item => this.buildDisplayItem(item, isBad))
                .filter(Boolean)
                .sort((a, b) => {
                    const pa = a.tag_label.replace(/[^0-9.]/g, '').split('.');
                    const pb = b.tag_label.replace(/[^0-9.]/g, '').split('.');
                    return (parseInt(pa[0]) || 0) - (parseInt(pb[0]) || 0)
                        || (parseInt(pa[1]) || 0) - (parseInt(pb[1]) || 0);
                });
        }
    },
    watch: {
        subCodes: {
            handler(newVal) {
                if (this.scoringData === null && newVal && newVal.length > 0) {
                    this.fetchScoring();
                }
            },
            immediate: true
        },
        campaignId() {
            if (this.scoringData === null && this.subCodes.length > 0) this.fetchScoring();
        },
        rawScoringData: {
            handler(val) {
                // 데이터가 실제로 있을 때 최초 1회만 build
                if (!this._initialBuilt && val && val.length > 0) {
                    this._initialBuilt = true;
                    this.buildScoringItems();
                }
            },
            immediate: true
        }
    },
    methods: {
        // ── display item 생성 ──
        makeItem(sub_code, tag_label, tag_class, display_text, text_class = '') {
            return { sub_code, tag_label, tag_class, display_text, text_class };
        },

        buildDisplayItem(item, isBad) {
            const tag = this.resolveTagLabel(item);
            const t = (k) => this.$t(k);

            // 1) 미답변 / 해당없음
            if (item.unanswered) return this.makeItem(item.sub_code, tag, 'mlt-tag--fail', t('답변 안함'), 'mlt-text--na');
            if (item.not_applicable) return this.makeItem(item.sub_code, tag, 'mlt-tag--na', t('해당없음'), 'mlt-text--na');

            // 2) 매우미흡 / 미흡
            if (isBad) return this.buildBadLevelItem(item, tag);

            // 3) 보통 / 우수
            return this.buildGoodLevelItem(item, tag);
        },

        // 매우미흡/미흡 표시 로직
        buildBadLevelItem(item, tag) {
            const cb = item.cb_data;
            const t = (k) => this.$t(k);

            // TYPE3 checkbox + "예" 답변 → positiveReverseName
            if (cb && !cb.isNegativeAnswer && cb.positiveReverseName) {
                const missing = item.cb_missing_attach || item.radio_missing_attach;
                const text = missing ? `${cb.positiveReverseName} (${t('증빙 누락')})` : cb.positiveReverseName;
                return this.makeItem(item.sub_code, tag,
                    'mlt-tag--warn',
                    text,
                    missing ? 'mlt-text--warn' : '');
            }

            // TYPE1/2/3 score=0 → morueum 텍스트 (증빙 누락 시 표기 추가)
            if (item.morueum_ev_name && item.score === 0) {
                const missing = item.radio_missing_attach;
                const text = missing ? `${item.morueum_ev_name} (${t('증빙 누락')})` : item.morueum_ev_name;
                return this.makeItem(item.sub_code, tag,
                    missing ? 'mlt-tag--warn' : 'mlt-tag--fail',
                    text,
                    missing ? 'mlt-text--warn' : '');
            }

            return null;
        },

        // 보통/우수 표시 로직
        buildGoodLevelItem(item, tag) {
            const cb = item.cb_data;
            const t = (k) => this.$t(k);

            // score 0 → 미표시
            if (item.score === 0) return null;

            // TYPE3 checkbox → 충족 목록
            if (cb && cb.satisfiedCount > 0) {
                return this.makeItem(item.sub_code, tag, 'mlt-tag--pass', cb.satisfiedEvNames);
            }

            // TYPE1/2 증빙 누락
            if (item.missing_attach_names && item.missing_attach_names.length > 0) {
                const text = item.missing_attach_names.map(n => `${n} (${t('증빙 누락')})`).join(', ');
                return this.makeItem(item.sub_code, tag, 'mlt-tag--warn', text, 'mlt-text--warn');
            }

            // TYPE1/2 정상 충족
            if (item.ev_name) {
                return this.makeItem(item.sub_code, tag, 'mlt-tag--pass', item.ev_name);
            }

            return null;
        },

        evName(r) {
            const loc = this.$i18n?.locale;
            if (loc === 'en') return r.ev_name_e || r.ev_name;
            if (loc === 'ca') return r.ev_name_c || r.ev_name;
            return r.ev_name;
        },
        evReverseName(r) {
            const loc = this.$i18n?.locale;
            if (loc === 'en') return r.ev_reverse_name_e || r.ev_reverse_name;
            if (loc === 'ca') return r.ev_reverse_name_c || r.ev_reverse_name;
            return r.ev_reverse_name;
        },
        buildScoringItems() {
            const raw = this.rawScoringData;
            if (!raw || raw.length === 0) {
                this.scoringItems = [];
                return;
            }

            const subsMap = {};
            (this.subsData || []).forEach(s => {
                if (s && s.sub_code) subsMap[s.sub_code] = s;
            });

            const grouped = {};
            raw.forEach(row => {
                const sc = row.sub_code;
                if (!grouped[sc]) {
                    grouped[sc] = {
                        sub_code: sc,
                        label: row.label || row.sub_name || sc,
                        rows: [],
                        risk: row.risk || false,
                        cap_score: row.cap_score || 0,
                        score_scaler: row.score_scaler || 1
                    };
                }
                grouped[sc].rows.push(row);
            });

            this.scoringItems = Object.values(grouped).map(group => {
                const subData = subsMap[group.sub_code];
                const rows = group.rows;
                const allNA = rows.every(r => r.not_applicable === true);
                const satisfied = rows.some(r => r.satisfied === true && r.not_applicable !== true);
                const score = rows.reduce((sum, r) => sum + (r.not_applicable ? 0 : (r.score || 0)), 0);

                // 보통/우수용
                const evNames = rows
                    .filter(r => r.ev_name && r.satisfied === true && r.ev_score > 0 && r.not_applicable !== true)
                    .map(r => this.evName(r));
                // 매우미흡/미흡용: 부정답변(아니오/모름) 표시 문구
                //   - morueumRow: result=='모름' 행 (cbIsNegativeAnswer 판정용으로 유지)
                //   - negativeEvName: 실제 satisfied 된 아니오/모름 행의 ev_name (없으면 모름 행 fallback)
                //     → 아니오 답에 모름 문구가 뜨거나, 모름 행이 없어 빈칸이 되던 문제 해결.
                //       "result=='아니오" 부분일치로 "아니오, …" 변형 답변도 커버.
                const morueumRow = rows.find(r => r.formula && r.formula.includes("result=='모름'") && r.ev_score === 0 && r.ev_name);
                const negativeEvRow = rows.find(r => r.ev_score === 0 && r.ev_name && r.satisfied === true
                    && r.formula && (r.formula.includes("result=='아니오") || r.formula.includes("result=='모름'")));
                const negativeEvName = negativeEvRow
                    ? this.evName(negativeEvRow)
                    : (morueumRow ? this.evName(morueumRow) : '');

                // checkbox 자동 감지: structure[0] 항목이 2개 이상 + ev_score가 모두 동일 (radio는 score가 다름)
                const checkboxRows = rows.filter(r => r.formula && r.formula.includes('structure[0]') && r.ev_score > 0 && r.not_applicable !== true);
                const allSameScore = checkboxRows.length >= 2 && checkboxRows.every(r => r.ev_score === checkboxRows[0].ev_score);
                const isCheckbox = allSameScore;

                // 증빙 누락: subs의 result로 부정 답변 판단
                const subResult = subData?.sub_data_contents?.result;
                const isNegativeAnswer = (typeof subResult === 'string' && subResult.startsWith('아니오')) || subResult === '모름' || subResult === '해당없음';
                // radio(단일) 전용: 답변했으나 미충족(satisfied=false)인데 증빙이 없는 경우.
                //   checkbox 는 cbMissingAttach 가 opt/sub 첨부 위치까지 보고 판정하므로 여기서 제외한다.
                //   (checkbox 옵션이 satisfied=true 면 백엔드가 이미 증빙 충분으로 인정한 것 →
                //    attachment_position='opt' 라도 sub 공용 첨부로 충족됐을 수 있어, opt_attach_count=0
                //    만 보고 누락 처리하면 오탐. 실제 H1.41_n: opt=0 / sub=4 / satisfied=true 인데 경고 발생)
                const missingAttachRows = (isNegativeAnswer || isCheckbox) ? [] : rows
                    .filter(r => r.is_required === true && r.ev_score > 0
                        && r.satisfied === false
                        && r.not_applicable !== true
                        && r[`${r.attachment_position}_attach_count`] < 1
                        && r.ev_name);
                // 최고 배점 1개만 (radio 하위 항목 중복 방지)
                // 최고 배점의 ev_reverse_name (증빙 누락 = 아니오 취급 = 미충족 문구)
                const topMissingRow = [...missingAttachRows].sort((a, b) => (b.ev_score || 0) - (a.ev_score || 0))[0];
                const topMissingReverseName = topMissingRow ? this.evReverseName(topMissingRow) : null;
                const missingAttachNames = topMissingReverseName ? [topMissingReverseName] : [];
                // 하단 가이드 경고용: ev_reverse_name이 null이어도 누락 사실은 플래그
                const radioMissingAttach = missingAttachRows.length > 0;
                // checkbox 증빙 누락 감지:
                // subData 있으면 실제 선택 목록으로 정밀 판단, 없으면 첨부 0인 필수 항목으로 판단
                const selectedCbNames = subData?.sub_data_contents?.structure?.[0]?.element_contents?.[0]?.result;
                const selectedCbArr = Array.isArray(selectedCbNames) ? selectedCbNames : [];
                // checkbox 증빙 누락: sub=공용 1개면 OK / opt=선택 개수 vs 증빙 개수 비교
                // formula에서 checkbox 라벨 추출 (selectedCbArr과 ev_name은 텍스트가 다름)
                const cbIsSelected = (r) => selectedCbArr.some(name => r.formula?.includes(name));
                const cbMissingAttach = (() => {
                    if (!isCheckbox || isNegativeAnswer) return false;
                    const pos = checkboxRows[0]?.attachment_position;
                    if (pos === 'sub') {
                        return (checkboxRows[0]?.sub_attach_count || 0) < 1;
                    }
                    // opt: 사용자 선택 개수 > 증빙 개수 → 누락
                    if (selectedCbArr.length > 0) {
                        const attachedCount = checkboxRows.filter(r =>
                            r.ev_score > 0 && cbIsSelected(r) && (r.opt_attach_count || 0) >= 1
                        ).length;
                        return selectedCbArr.length > attachedCount;
                    }
                    // fallback: subData 없을 때
                    return checkboxRows.some(r => r.is_required === true && r.ev_score > 0
                        && (r.opt_attach_count || 0) < 1 && (r.sub_attach_count || 0) < 1);
                })();
                let cbData = null;
                if (isCheckbox) {
                    const satisfiedCbRows = checkboxRows.filter(r => r.satisfied === true);
                    const satisfiedCheckboxCount = satisfiedCbRows.length;
                    const yesOnlyRow = rows.find(r => r.formula && /^result\s*==\s*'예'$/.test(r.formula.trim()));

                    // 충족된 ev_name 목록
                    const satisfiedEvNames = satisfiedCbRows.map(r => this.evName(r)).filter(Boolean);

                    // 내역 합산 대상 sub_code (H1.22_n, H1.22v2, H1.62v2)
                    const detailMergeSubs = ['H1.22_n', 'H1.22v2', 'H1.62v2'];
                    let mergedEvName = '';
                    if (detailMergeSubs.includes(group.sub_code) && checkboxRows.length > 3 && satisfiedEvNames.length > 0) {
                        // "활동을 시행함 (내역 : A)" 형태에서 prefix와 detail 분리 후 합산
                        const detailRegex = /^(.+?)\s*\(내역\s*:\s*(.+?)\)$/;
                        const prefix = satisfiedEvNames[0].match(detailRegex)?.[1] || satisfiedEvNames[0];
                        const details = satisfiedEvNames.map(name => {
                            const m = name.match(detailRegex);
                            return m ? m[2].trim() : name;
                        });
                        mergedEvName = `${prefix}\n- ${details.join('\n- ')}`;
                    } else {
                        mergedEvName = satisfiedEvNames.join('\n');
                    }

                    // 미흡/매우미흡용: (result=='아니오' || result=='예') && ev_score=0인 행의 ev_reverse_name
                    const aniyoRow = rows.find(r => r.formula && r.formula.includes("result=='아니오") && r.ev_score === 0);
                    const yesZeroRow = rows.find(r => r.formula && /^result\s*==\s*'예'$/.test(r.formula.trim()) && r.ev_score === 0);
                    const cbReverseRow = aniyoRow || yesZeroRow;
                    const cbPositiveReverseName = cbReverseRow ? this.evReverseName(cbReverseRow) : '';
                    // 부정 답변(아니오/모름) 판정: df_subs.result(L389 isNegativeAnswer) 우선.
                    //   첨부필수 문항에서 모름/아니오 eval이 '첨부 없음'으로 satisfied=false 강등돼도
                    //   답변(result) 기준으로 부정답변을 인식 → 아니오 reverse_name 오노출 방지.
                    const cbIsNegativeAnswer = isNegativeAnswer
                        || (aniyoRow && aniyoRow.satisfied === true)
                        || (morueumRow && morueumRow.satisfied === true);

                    cbData = {
                        totalOptions: checkboxRows.length,
                        satisfiedCount: satisfiedCheckboxCount,
                        satisfiedEvNames: mergedEvName,
                        positiveReverseName: cbPositiveReverseName,
                        isNegativeAnswer: cbIsNegativeAnswer
                    };
                }

                // 미답변 판단
                const result = subData?.sub_data_contents?.result;
                const isEmptyResult = !result || result === '' || result === null
                    || (Array.isArray(result) && result.length === 0);
                const unanswered = !allNA && isEmptyResult;

                return {
                    sub_code: group.sub_code,
                    label: group.label,
                    score,
                    cap_score: group.cap_score,
                    satisfied,
                    not_applicable: allNA,
                    is_core: group.risk,
                    ev_name: evNames.join(', '),
                    morueum_ev_name: negativeEvName,
                    missing_attach_names: missingAttachNames,
                    cb_missing_attach: cbMissingAttach,
                    radio_missing_attach: radioMissingAttach,
                    cb_data: cbData,
                    unanswered
                };
            });
        },

        refresh() {
            this._initialBuilt = false;
            if (this.scoringData !== null) {
                this.$emit('refresh');
            } else {
                this.fetchScoring();
            }
        },

        formatSubCode(subCode) {
            if (!subCode) return '';
            // "H1.9v2" → "Q1.9", "H1.10v2" → "Q1.10"
            const cleaned = subCode.replace(/v\d+$/, '');
            const match = cleaned.match(/[A-Za-z]*(\d+\.\d+)/);
            return match ? `Q${match[1]}` : subCode;
        },

        resolveTagLabel(item) {
            // sub_code에서 추출한 숫자와 실제 label이 어긋나는 케이스가 있어
            // (예: H1.22v2 → Q1.23) group.label(= row.label || sub_name)을 우선 사용
            const label = item.label;
            if (typeof label === 'string' && /^Q\d+\.\d+/.test(label.trim())) {
                return label.trim();
            }
            return this.formatSubCode(item.sub_code);
        },

        async fetchScoring() {
            if (!this.companyId || !this.campaignId || !this.projectCode || this.subCodes.length === 0) return;

            this.loading = true;
            this.hasError = false;
            try {
                const res = await this.$axios.get('/v2/scoring', {
                    params: {
                        company_id: this.companyId,
                        campaign_id: this.campaignId,
                        project_code: this.projectCode,
                        sub_codes: this.subCodes.join(','),
                        live: true
                    }
                });

                const data = res.data;
                let rawItems = [];
                if (data?.scoring?.scoring_pd) {
                    rawItems = [...rawItems, ...data.scoring.scoring_pd];
                }
                if (data?.risk?.risk_pd) {
                    rawItems = [...rawItems, ...data.risk.risk_pd];
                }
                this.fetchedItems = rawItems;
            } catch (error) {
                console.error('ManagementLevelTable scoring fetch error:', error);
                this.hasError = true;
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.mlt-root {
    width: 100%;
}

.mlt-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}

.mlt-guide {
    flex: 1;
    min-width: 0;
    font-size: 0.82rem;
    line-height: 1.45;
    color: #5d4037;
    word-break: keep-all;
}

.mlt-refresh-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    background: #fff;
    color: #2c2c2c;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    font-size: 0.83rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover:not(:disabled) {
        background: #f4f4f4;
        border-color: #888;
        color: #111;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
}

.mlt-wrap {
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

// Header bar
.mlt-header {
    display: flex;
    min-width: 500px;
}

.mlt-level {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 10px 8px;
    font-size: 0.88rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.92);
    background-color: #3a3a3a;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;

    &:last-child {
        border-right: none;
    }
}

.mlt-level--active {
    color: #111 !important;
    font-weight: 700;
    font-size: 0.92rem;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: var(--accent, #333);
    }
}

// Loading & Empty
.mlt-loading, .mlt-empty {
    padding: 16px;
    text-align: center;
    font-size: 0.85rem;
    color: #888;
    background: #f8f8f8;
    border-top: 1px solid #eee;
}

// Detail area
.mlt-detail {
    padding: 12px 16px;
    background: #f8f9fa;
    border-top: 1px solid #e8e8e8;
}

.mlt-item {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 8px 0;
    line-height: 1.55;

    & + .mlt-item {
        border-top: 1px solid #eaeaea;
    }
}

// Tag badge
.mlt-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 56px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    position: relative;
    top: -1px;
    margin-top: 2px;
}

.mlt-tag--pass {
    background: #c8e6c9;
    color: #1b5e20;
    border: 1px solid #a5d6a7;
}

.mlt-tag--fail {
    background: #ffcdd2;
    color: #b71c1c;
    border: 1px solid #ef9a9a;
}

.mlt-tag--unanswered {
    background: #ffcdd2;
    color: #b71c1c;
    border: 1px solid #ef9a9a;
}

.mlt-tag--warn {
    background: #fff3cd;
    color: #856404;
    border: 1px solid #ffc107;
}

.mlt-tag--na {
    background: #e8e8e8;
    color: #616161;
    border: 1px solid #d0d0d0;
}

// Text
.mlt-text {
    font-size: 0.87rem;
    color: #2c2c2c;
    word-break: keep-all;
    line-height: 1.55;
    white-space: pre-line;
}

.mlt-text--na {
    color: #9e9e9e;
    font-style: italic;
}

.mlt-text--warn {
    color: #856404;
}

// NA group separator
.mlt-na-group {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #ddd;
}

.mlt-item--na {
    border-top: none !important;
}

.mlt-notice {
    margin-top: 12px;
    padding: 10px 12px;
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 4px;
    font-size: 0.82rem;
    color: #5d4037;
    line-height: 1.55;
}

.mlt-notice--warn {
    background: #fce4ec;
    border-color: #ef9a9a;
    color: #b71c1c;
}

.mlt-notice-icon {
    color: #f9a825;
    margin-right: 4px;
}

.mlt-notice-icon--warn {
    color: #c62828;
    margin-right: 4px;
}
</style>
